import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useJWTAuth } from '../JWTAuthContext';

const QuickActions: React.FC = () => {
  const { user } = useJWTAuth();
  const [isExpanded, setIsExpanded] = useState(false);

  // All actions
  const allActions = [
    { to: '/custom-quiz-history', label: 'Custom Quiz', icon: 'fas fa-cog', color: '#0dcaf0', show: true },
    { to: '/pyq-history', label: 'PYQ', icon: 'fas fa-archive', color: '#6f42c1', show: true },
    { to: '/mock-test', label: 'Mock Test', icon: 'fas fa-clipboard-list', color: '#198754', show: true },
    { to: '/study-plan', label: 'Study Plan', icon: 'fas fa-calendar-plus', color: '#dc3545', show: true },
    { to: '/unattempted-quizzes', label: 'Available Quizzes', icon: 'fas fa-tasks', color: '#0d6efd', show: true },
    { to: '/performance', label: 'Performance', icon: 'fas fa-chart-bar', color: '#ffc107', show: true },
    { to: '/report-card', label: 'Report Card', icon: 'fas fa-file-alt', color: '#6c757d', show: true },
    { to: '/ai-tutor', label: 'AI Mentor', icon: 'fas fa-robot', color: '#0046f5', show: true }
  ];

  return (
    <div className="row g-4">
      <div className="col-md-12">
        <div className="card border-0 shadow-sm" style={{ borderRadius: '15px', cursor: 'pointer' }}>
          <div className="card-header bg-transparent border-0 p-4" onClick={() => setIsExpanded(!isExpanded)}>
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="fw-bold mb-0">
                <i className="fas fa-rocket me-2 text-primary"></i>
                Quick Actions
              </h5>
              <div 
                className="d-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: '36px',
                  height: '36px',
                  background: '#667eea15',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease'
                }}
              >
                <i 
                  className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}
                  style={{ 
                    color: '#667eea',
                    opacity: 0.9,
                    transition: 'transform 0.3s ease'
                  }}
                ></i>
              </div>
            </div>
          </div>
          <div className="card-body p-4 pt-1">
            {/* Compact View - Icons Only in Single Line */}
            {!isExpanded && (
              <div className="d-flex justify-content-between align-items-center gap-2">
                {allActions.filter(action => action.show).map((action, index) => (
                  <Link 
                    key={index}
                    to={action.to} 
                    className="flex-fill text-center text-decoration-none"
                    title={action.label}
                    style={{ transition: 'all 0.3s ease' }}
                    onMouseEnter={(e) => {
                      const icon = e.currentTarget.querySelector('.action-icon-wrapper') as HTMLElement;
                      if (icon) {
                        icon.style.transform = 'scale(1.1) translateY(-4px)';
                        icon.style.background = action.color;
                        icon.style.color = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const icon = e.currentTarget.querySelector('.action-icon-wrapper') as HTMLElement;
                      if (icon) {
                        icon.style.transform = 'scale(1) translateY(0)';
                        icon.style.background = `${action.color}15`;
                        icon.style.color = action.color;
                      }
                    }}>
                    <div 
                      className="action-icon-wrapper rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                      style={{
                        width: '56px',
                        height: '56px',
                        background: `${action.color}15`,
                        color: action.color,
                        transition: 'all 0.3s ease'
                      }}>
                      <i className={`${action.icon} fa-lg`}></i>
                    </div>
                    <small 
                      className="d-block fw-semibold" 
                      style={{ 
                        fontSize: '14px', 
                        color: '#4b4f53ff',
                        lineHeight: '1.2',
                        fontWeight: 'bold'
                      }}>
                      {action.label}
                    </small>
                  </Link>
                ))}
              </div>
            )}

            {/* Expanded View - Horizontal Cards */}
            {isExpanded && (
              <div className="row g-3 animate__animated animate__fadeIn">
                {allActions.filter(action => action.show).map((action, index) => (
                  <div key={index} className="col-md-6 col-lg-4 col-xl-3">
                    <Link 
                      to={action.to} 
                      className="text-decoration-none d-block h-100"
                      style={{ transition: 'all 0.3s ease' }}
                      onMouseEnter={(e) => {
                        const card = e.currentTarget.querySelector('.action-card') as HTMLElement;
                        if (card) {
                          card.style.transform = 'translateY(-4px)';
                          card.style.boxShadow = `0 8px 24px ${action.color}30`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        const card = e.currentTarget.querySelector('.action-card') as HTMLElement;
                        if (card) {
                          card.style.transform = 'translateY(0)';
                          card.style.boxShadow = 'none';
                        }
                      }}>
                      <div 
                        className="action-card p-3 rounded-3 d-flex align-items-center gap-3 h-100"
                        style={{ 
                          background: `linear-gradient(135deg, ${action.color}15 0%, ${action.color}05 100%)`,
                          border: `2px solid ${action.color}30`,
                          transition: 'all 0.3s ease'
                        }}>
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: '50px',
                            height: '50px',
                            background: action.color,
                            color: 'white'
                          }}>
                          <i className={`${action.icon} fa-lg`}></i>
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-bold" style={{ color: action.color, fontSize: '15px' }}>
                            {action.label}
                          </div>
                          <small className="text-muted" style={{ fontSize: '11px' }}>
                            {action.label === 'PYQ' && 'Previous Year Questions'}
                            {action.label === 'Custom Quiz' && 'Create your own quiz'}
                            {action.label === 'Available Quizzes' && 'Browse all quizzes'}
                            {action.label === 'Performance' && 'View your analytics'}
                            {action.label === 'Mock Test' && 'Practice full tests'}
                            {action.label === 'Study Plan' && 'Schedule your study'}
                            {action.label === 'Report Card' && 'Check your results'}
                            {action.label === 'AI Mentor' && 'Get personalized guidance'}
                          </small>
                        </div>
                        <i className="fas fa-chevron-right" style={{ color: action.color, fontSize: '12px' }}></i>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* <div className="col-md-4">
        <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
          <div className="card-header bg-transparent border-0 p-4">
            <h5 className="fw-bold mb-0">
              <i className="fas fa-calendar-alt me-2 text-primary"></i>
              Study Plan : Today's Goal
            </h5>
          </div>
          <div className="card-body p-4">
            <div className="text-center">
              Commented out for now - Coming Soon
              <div className="position-relative d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: '100px', height: '100px' }}>
                <svg className="position-absolute" width="100" height="100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="#e9ecef"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="#667eea"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray="283"
                    strokeDashoffset="85"
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                  />
                </svg>
                <span className="fw-bold fs-4">70%</span>
              </div>
              <p className="text-muted mb-3">Daily Progress</p>
              <small className="text-success">
                <i className="fas fa-check-circle me-1"></i>
                7 out of 10 questions completed
              </small>
              
              <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '140px' }}>
                <i className="fas fa-clock text-muted mb-3" style={{ fontSize: '3rem', opacity: '0.5' }}></i>
                <h6 className="text-muted mb-2">Coming Soon</h6>
                <p className="text-muted text-center small mb-0">
                  Study Plan: Daily goal tracking and progress insights will be available soon.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default QuickActions;